import { notifications } from "@mantine/notifications";
import { PRIZE_TYPE_VALUES } from "@mip-tg-bot-turntable/platform-api";
import { computeProbabilities, type PrizeCandidate } from "@mip-tg-bot-turntable/utils";
import { AdminApiError, adminErrText } from "../../../api.ts";
import { fieldNameZh } from "../../../lib/fieldNames.ts";

/** prize.code 是纯内部唯一标识，无业务语义（引擎按 weight+id 抽样，不依赖 code）。
 *  运营无需手填；此函数生成 8 位小写字母+数字随机串，极低概率撞时前端预校验拦截。 */
const CODE_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";
export const genPrizeCode = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  let out = "";
  for (const b of bytes) out += CODE_ALPHABET[b % CODE_ALPHABET.length];
  return out;
};

export interface PrizeRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  type: "thanks" | "extra_spins" | "physical" | "virtual";
  value_amount: number;
  value_currency: string | null;
  weight: number;
  stock: number | null;
  daily_limit: number | null;
  is_enabled: boolean;
  is_jackpot: boolean;
  requires_shipping: boolean;
  image_url: string | null;
  title_mode: "text" | "image" | "image_text";
  display_color: string | null;
  display_icon: string | null;
  display_order: number;
  prize_metadata: Record<string, unknown>;
  i18n_name: Record<string, string>;
  i18n_subtitle: Record<string, string>;
  starts_at: string | null;
  ends_at: string | null;
}

export interface CreateForm {
  code: string;
  name: string;
  description: string;
  type: string;
  weight: number;
  valueAmount: number;
  valueCurrency: string;
  stock: number | string;
  dailyLimit: number | string;
  isJackpot: boolean;
  requiresShipping: boolean;
  wgPay: boolean;
  auditRate: number;
  claimInstructions: Record<string, string>;
  imageUrl: string;
  titleMode: "text" | "image" | "image_text";
  displayColor: string;
  displayIcon: string;
  displayOrder: number;
  i18nName: Record<string, string>;
  i18nSubtitle: Record<string, string>;
  startsAt: string;
  endsAt: string;
}

export interface EditForm {
  type: string;
  name: string;
  description: string;
  weight: number;
  valueAmount: number;
  valueCurrency: string;
  stock: number | string;
  dailyLimit: number | string;
  isEnabled: boolean;
  isJackpot: boolean;
  requiresShipping: boolean;
  wgPay: boolean;
  auditRate: number;
  claimInstructions: Record<string, string>;
  imageUrl: string;
  titleMode: "text" | "image" | "image_text";
  displayColor: string;
  displayIcon: string;
  displayOrder: number;
  i18nName: Record<string, string>;
  i18nSubtitle: Record<string, string>;
  startsAt: string;
  endsAt: string;
}

/** 奖品多语言名称支持的语言（与印度市场 bot_text 一致）。 */
export const PRIZE_I18N_LANGS = ["en-IN", "zh", "hi"] as const;

/** 清理 i18n 文案表单值：去掉空字符串项，避免 Mini App 把空串当文案。 */
export const buildI18nMap = (rec: Record<string, string>): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(rec)) {
    const t = v.trim();
    if (t !== "") out[k] = t;
  }
  return out;
};

/** wg.pay 开关 + 稽核倍率 + 领奖说明语言 map -> prize_metadata 对象（提交给 API）。 */
export const buildPrizeMetadata = (
  wgPay: boolean,
  claimInstructions: Record<string, string>,
  auditRate: number,
): Record<string, unknown> => {
  const meta: Record<string, unknown> = {};
  if (wgPay) meta["code"] = "wg.pay";
  const ci = buildI18nMap(claimInstructions);
  if (Object.keys(ci).length > 0) meta["claimInstructions"] = ci;
  // 稽核倍率仅对 wg.pay 现金奖有意义；默认 1 不写入，避免元数据冗余。
  if (wgPay && Number.isFinite(auditRate) && auditRate > 1) {
    meta["auditRate"] = String(auditRate);
  }
  return meta;
};

/** 从 prize_metadata 读回 wg.pay 开关、稽核倍率、领奖说明语言 map（回填编辑表单）。
 * 存量纯字符串向前兼容：自动迁移到 en-IN 字段。 */
export const readPrizeMetadata = (
  meta: Record<string, unknown> | null | undefined,
): { wgPay: boolean; claimInstructions: Record<string, string>; auditRate: number } => {
  const m = meta ?? {};
  const rawAudit = m["auditRate"];
  const auditNum =
    typeof rawAudit === "number" ? rawAudit : typeof rawAudit === "string" ? Number(rawAudit) : NaN;
  const rawCi = m["claimInstructions"];
  let claimInstructions: Record<string, string>;
  if (typeof rawCi === "object" && rawCi !== null) {
    claimInstructions = rawCi as Record<string, string>;
  } else if (typeof rawCi === "string" && rawCi.trim() !== "") {
    claimInstructions = { "en-IN": rawCi };
  } else {
    claimInstructions = {};
  }
  return {
    wgPay: m["code"] === "wg.pay",
    claimInstructions,
    auditRate: Number.isFinite(auditNum) && auditNum >= 1 ? auditNum : 1,
  };
};

/** 批量创建行数据。stock / dailyLimit 用字符串，空串表示无限。 */
export interface BulkRow {
  code: string;
  name: string;
  type: string;
  weight: number;
  valueAmount: number;
  stock: string;
  dailyLimit: string;
  imageUrl: string;
  displayColor: string;
  requiresShipping: boolean;
  _error?: string;
  _uploading?: boolean;
}

export const PRIZE_TYPES: string[] = [...PRIZE_TYPE_VALUES];

export const PRIZE_TYPE_COLOR: Record<string, string> = {
  thanks: "gray",
  extra_spins: "blue",
  physical: "orange",
  virtual: "violet",
};

export const useProbabilities = (rows: PrizeRow[]): Map<string, number> =>
  computeProbabilities(
    rows.map(
      (p): PrizeCandidate => ({
        id: p.id,
        weight: p.weight,
        isExhausted: !p.is_enabled,
        type: p.type,
      }),
    ),
  );

export const showIssueErrors = (e: unknown): void => {
  const err = e as AdminApiError;
  if (err.code === "INVALID_REQUEST" && err.issues.length > 0) {
    for (const issue of err.issues) {
      const label = fieldNameZh(issue.path.join("."));
      notifications.show({ color: "red", message: `${label}：${issue.message}` });
    }
  } else {
    notifications.show({ color: "red", message: adminErrText(err.code, err.message) });
  }
};

export const emptyBulkRow = (): BulkRow => ({
  code: genPrizeCode(),
  name: "",
  type: "thanks",
  weight: 1,
  valueAmount: 0,
  stock: "",
  dailyLimit: "",
  imageUrl: "",
  displayColor: "",
  requiresShipping: false,
});
